// src/components/Settings.jsx
import { use, useEffect, useState } from "react";
import "./Settings.css";
import axios from "axios";
import UploadPdf from "./UploadPdf";
import UploadedFilesTable from "./UploadedFile";
import HeygenLiveAvatar from "./HeygenLiveAvatar";
import GoogleFilesTable from "./GoogleFilesTable";
import VoiceUploader from "./VoiceUploader";

const TONE_KEYS = ["humor", "honesty", "sarcasm", "conciseness", "empathy"];
const DEFAULT_VALUES = TONE_KEYS.reduce((acc, k) => ({ ...acc, [k]: 50 }), {});
const MODEL_CHOICES = [
  { value: "openai-gpt-4o", label: "OpenAI (gpt-4o)" },
  { value: "grok-grok-4", label: "Grok (grok-4)" },
  { value: "gemini-live-2.5-flash-preview-(realtime)", label: "Gemini (live-2.5-flash-preview) (realtime)"},
  { value: "openai-gpt-4o-realtime-preview-(realtime)", label: "OpenAI (gpt-4o-realtime-preview) (realtime)"},
];

export default function Settings({ id }) {
  const [values, setValues] = useState(null);
  const [latencys, setLatencies] = useState(null);
  const [model, setModel] = useState("openai-gpt-4o");
  const [assistantName, setAssistantName] = useState("");
  const [ttsVoice, setTtsVoice] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [isPiiMaskingEnabled, setIsPiiMaskingEnabled] = useState(false);

  const [notionData, setNotionData] = useState(null);

  const [fileData, setFileData] = useState(null);

  const [voices, setVoices] = useState([]);
  const [error, setError] = useState(null);

  const [showStats, setShowStats] = useState(false);
  const [showIntegration, setShowIntegration] = useState(false);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/elevenlabs-voices/`);
        setVoices(response.data);
      } catch (err) {
        console.error("Error fetching voices:", err);
        setError(err);
      }
    };

    fetchVoices();
  }, []); 

  useEffect(() => {
    if (!loaded) {
      const saved = localStorage.getItem("assistantToneSettings");
      const savedModel = localStorage.getItem("assistantModel");
      if (saved) setValues(JSON.parse(saved));
      else setValues(DEFAULT_VALUES);

      if (savedModel) setModel(savedModel);
    }
  }, [loaded]);

  useEffect(() => {
    if (values) {
      localStorage.setItem("assistantToneSettings", JSON.stringify(values));
      localStorage.setItem("assistantModel", model);
    }
  }, [values, model]);

  const handleChange = (key, val) => {
    const num = Number(val);
    if (!isNaN(num)) {
      setValues((prev) => ({
        ...prev,
        [key]: Math.max(0, Math.min(100, Math.round(num))),
      }));
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/assistant/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data) {
        const toneData = TONE_KEYS.reduce(
          (acc, key) => ({
            ...acc,
            [key]:
              response.data[key] ??
              response.data.tone_settings?.[key] ??
              DEFAULT_VALUES[key],
          }),
          {}
        );

        setValues(toneData);
        setLatencies({
          average_stt_latency: response.data.average_stt_latency || 0,
          average_llm_latency: response.data.average_llm_latency || 0,
          average_tts_latency: response.data.average_tts_latency || 0,
          average_latency: response.data.average_latency || 0,
        });
        setModel(response.data.model || "openai-gpt-4o");
        setAssistantName(response.data.name || ""); 
        setTtsVoice(response.data.tts_voice || "");
        setNotionData(response.data.notion || null);
        setFileData(response.data.file || null);
        setIsPiiMaskingEnabled(response.data.is_pii_masking_enabled || false);
        setLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      setValues(DEFAULT_VALUES);
      setModel("openai-gpt-4o");
      setAssistantName("");
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/assistant/${id}/`,
        { ...values, model, name: assistantName , tts_voice: ttsVoice ,is_pii_masking_enabled: isPiiMaskingEnabled},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      alert("Settings saved successfully!");
      fetchSettings();
    } catch (error) {
      alert("Error saving settings. Please try again.");
      console.error("Error saving settings:", error);
    }
  };

  const handleConnectNotion = async () => {
    try {
      const token = localStorage.getItem("accessToken"); 
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/integration/notion/get-url/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const authUrl = response.data.auth_url;
      window.open(authUrl, "_blank"); 

    } catch (error) {
      console.error("Error getting Notion auth URL:", error);
      alert("Failed to connect to Notion.");
    }
  };

  const handleConnectGoogleDocs = async () => {
    try {
      const token = localStorage.getItem("accessToken"); 
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/integration/google-docs/get-url/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const authUrl = response.data.auth_url;
      window.open(authUrl, "_blank"); 

    } catch (error) {
      console.error("Error getting Notion auth URL:", error);
      alert("Failed to connect to Notion.");
    }
  };


  if (error) return <div>Error loading voices.</div>;
  if (!values || !voices) return <p>Loading settings...</p>;

  return (
    <div className="settings-root">
      <h1>Assistant Settings</h1>

      {/* 🆕 Assistant name input */}
      <div className="assistant-name-input">
        <label>Assistant Name:</label>
        <input
          type="text"
          value={assistantName}
          onChange={(e) => setAssistantName(e.target.value)}
          placeholder="Enter assistant name"
          className="name-input"
        />
      </div>

      <label>{"STT: OpenAI (whisper-1)"}</label><br />

      <div className="model-selector">
        <label>LLM Model:</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="model-dropdown"
        >
          {MODEL_CHOICES.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>

      <label>{"TTS: ElevenLabs (eleven_turbo_v2_5)"}</label>

      <div className="model-selector">
        <label>ElevenLabs Voice:</label>
        <select
          value={ttsVoice}
          onChange={(e) => setTtsVoice(e.target.value)}
          className="model-dropdown"
        >
          {voices.map((choice) => (
            <option key={choice.id} value={choice.id}>
              {choice.name}
            </option>
          ))}
        </select>
      </div>


      <div className="pii-masking">
        <label>
          <input
            type="checkbox"
            checked={isPiiMaskingEnabled}
            onChange={(e) => setIsPiiMaskingEnabled(e.target.checked)}
          />
          Enable PII Masking
        </label>
      </div>

      <p className="muted">Adjust tones from 0 to 100</p>

      <div className="settings-controls">
        {TONE_KEYS.map((key) => (
          <div className="tone-row" key={key}>
            <div className="tone-label">
              <label>{key}</label>
              <span className="tone-value">{values[key]}</span>
            </div>

            <input
              type="range"
              min="0"
              max="100"
              value={values[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              className="tone-slider"
            />

            <input
              type="number"
              min="0"
              max="100"
              value={values[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              className="tone-number"
            />
          </div>
        ))}
      </div>

      <div className="settings-actions">
        <button onClick={handleSave} className="btn primary save-btn">
          Save
        </button>
      </div>

      <UploadPdf />


      <button 
        onClick={() => setShowStats(!showStats)} 
        className="btn secondary stats-toggle-btn"
      >
        {showStats ? "Hide" : "Show"} Performance Stats
      </button>

      <button 
        onClick={() => setShowIntegration(!showIntegration)} 
        className="btn secondary integration-toggle-btn"
      >
        {showIntegration ? "Hide" : "Show"} Integration
      </button>
      {showIntegration  && (
        <div className="integration">
          {notionData ? (
            <div>
              <h3>Notion Connected</h3>
              <p>Workspace Name: {notionData.workspace_name}</p>
            </div>
          ) : (
            <>
            <button onClick={handleConnectNotion}>
      Connect Notion
    </button>
            
            </>
          )}
          <button onClick={handleConnectGoogleDocs} className="btn secondary">
              Connect to Google Docs
            </button>
        </div>
      )}

      {showStats && (
        <div className="settings-stats">
          <h2>Performance Stats</h2>
          <ul>
            <li><strong>STT Latency:</strong> {latencys?.average_stt_latency.toFixed(3) ?? 0} s</li>
            <li><strong>LLM Latency:</strong> {latencys?.average_llm_latency.toFixed(3) ?? 0} s</li>
            <li><strong>TTS Latency:</strong> {latencys?.average_tts_latency.toFixed(3) ?? 0} s</li>
            <li><strong>Overall Latency:</strong> {latencys?.average_latency.toFixed(3) ?? 0} s</li>
            <li><strong>Files Uploaded:</strong> {fileData ? fileData.length : 0}</li>
            <li><strong>Notion Connected:</strong> {notionData ? "Yes" : "No"}</li>
        </ul>
        <UploadedFilesTable data={fileData || []} />
        <GoogleFilesTable/>
      </div>
      )}


      {/* <HeygenLiveAvatar /> */}

      <VoiceUploader/>

    </div>
  );
}
